/**
 * 이상한 방 - 오디오 엔진 (Web Audio 합성 래퍼)
 *
 * 근거 문서: 상태모델 7.0절(에셋 트래픽) · 컨셉시트 4.3절(오답 시 탄피 사운드)
 *
 * ## 오디오 파일을 받지 않는다
 *
 * 이 게임의 배포 방식은 **링크 하나로 폰에서 즉시 접속**이다(상태모델 7.0절이 GLB 트래픽을
 * 경계한 것과 같은 이유). mp3 하나가 30KB면 13종이 400KB이고, 그건 첫 화면이 뜨기 전에
 * 파티가 식는 크기다. 전부 합성하면 **에셋 용량이 0**이고, 대신 두 가지를 공짜로 얻는다.
 *
 *  1. **콤보마다 반음씩 올리기.** 샘플이면 피치 시프트 코드를 따로 짜야 하지만
 *     오실레이터는 주파수 하나만 곱하면 된다. 그게 이 게임의 도파민 장치의 핵심이다.
 *  2. **개발자가 귀로 튜닝하기.** 숫자를 바꾸고 새로고침하면 끝이다(`sounds.ts` 상단 표).
 *
 * ## 이 파일의 책임 경계
 *
 * | 층 | 답하는 질문 | 파일 |
 * |---|---|---|
 * | 사전 | **어떤 소리인가**(파형·주파수·길이) | `sounds.ts` |
 * | 엔진 | **어떻게 울리는가**(노드 조립·수명·음소거) | **이 파일** |
 * | 규칙 | **언제 울리는가** | `game/gameState.ts`의 `playSound` 효과 |
 *
 * 사전이 `SoundSpec`이라는 순수 데이터만 넘기므로 엔진은 게임을 모르고, 사전은 Web Audio를
 * 모른다. 덕분에 사전 테스트에 AudioContext가 필요 없다.
 *
 * ## 소리는 부가 기능이지 전제가 아니다
 *
 * AudioContext를 못 만드는 환경이 실제로 있다(구형 WebView, 기업 정책, 자동재생 차단).
 * 그런 브라우저에서 **게임은 정상 동작해야 한다.** 그래서 이 파일의 모든 실패 경로는
 * 던지지 않고 `false`를 돌려준다. 예외 하나가 렌더 루프를 멈추면 소리가 없는 게 아니라
 * 게임이 없는 것이 된다.
 *
 * ## 제스처 전에는 조용히 아무 일도 하지 않는다
 *
 * iOS·Chrome은 사용자 제스처 전의 AudioContext를 suspended로 붙잡는다. 그 상태에서
 * 재생을 시도하면 브라우저가 콘솔에 경고를 쌓고, 그 경고 더미에 진짜 버그가 묻힌다.
 * 그래서 **`unlock()`이 호출되기 전까지 AudioContext를 만들지도 않는다.**
 * `installUnlockGesture()`가 첫 탭에서 그것을 대신해 준다.
 */

import { sharedAudioContext } from './bgm'
import { clampUnit, installGestureOnce, readJsonSetting, writeJsonSetting } from './storage'

/* --- 소리 명세 (사전이 채우고 엔진이 읽는다) --------------------------------------- */

/** 쓰는 파형만 남긴다. `OscillatorType`의 `'custom'`은 PeriodicWave가 필요해 이 게임에 없다. */
export type Wave = 'sine' | 'square' | 'sawtooth' | 'triangle'

/** 쓰는 필터만 남긴다. 셋이면 탄피(밴드패스)·워프(로우패스)·스탬프(하이패스)가 다 나온다. */
export type FilterKind = 'lowpass' | 'highpass' | 'bandpass'

export type VoiceFilter = Readonly<{
  kind: FilterKind
  hz: number
  /** 있으면 `hz`에서 여기까지 훑는다(워프 스윕). 없으면 고정. */
  hzTo?: number
  /** 공진. 높을수록 좁고 금속적이다(탄피는 8 이상). */
  q?: number
}>

export type VoiceSource =
  /** 음정이 있는 소리. `hzTo`가 있으면 지수 글라이드(사람 귀는 주파수를 로그로 듣는다). */
  | Readonly<{ kind: 'tone'; wave: Wave; hz: number; hzTo?: number }>
  /** 음정이 없는 소리. 탄피·철컥·스탬프의 재료다. */
  | Readonly<{ kind: 'noise' }>

/**
 * 소리 한 겹.
 *
 * 겹을 나눈 이유는 연쇄를 표현하기 위해서다 - `delay`만 다른 같은 겹 셋이면 탄피가 세 번
 * 튀고, 주파수만 다른 겹 다섯이면 아르페지오가 된다. 시퀀서를 따로 만들 필요가 없다.
 */
export type Voice = Readonly<{
  source: VoiceSource
  /** 시작 지연(초). 아르페지오·탄피 튐·더블클릭을 전부 이걸로 만든다. */
  delay?: number
  /** 어택(초). 타악기적인 소리라 기본값은 거의 0이다. */
  attack?: number
  /** 감쇠(초). 이 겹의 길이 = `delay` + `attack` + `decay`. */
  decay: number
  /** 정점 게인(0~1). 스펙 게인과 마스터 게인이 다시 곱해진다. */
  gain: number
  filter?: VoiceFilter
}>

export type SoundSpec = Readonly<{
  /** 디버깅용 이름. 사전의 키와 같다. */
  id: string
  voices: readonly Voice[]
  /** 소리 하나의 상대 볼륨. 사전에서 소리끼리 균형을 맞출 때 이것만 만진다. */
  gain?: number
}>

/* --- 튜닝 상수 -------------------------------------------------------------------- */

/**
 * 엔진 쪽 상수. **소리의 성격이 아니라 재생 정책이다**(성격은 `sounds.ts`가 가진다).
 *
 * | 상수 | 올리면 | 내리면 |
 * |---|---|---|
 * | `DEFAULT_VOLUME` | 전체가 커진다 | 전체가 작아진다 |
 * | `MAX_CONCURRENT_SOUNDS` | 연타가 다 들린다 | 폰에서 끊김이 줄고 최신 소리만 남는다 |
 * | `STEAL_FADE_SECONDS` | 뺏길 때 뚝 끊기지 않는다 | 뺏기가 즉각적이다(딸깍 위험) |
 */

/** 기본 볼륨. 1.0은 겹이 여럿 겹칠 때 클리핑이 나므로 여유를 둔다. */
const DEFAULT_VOLUME = 0.7

/**
 * 동시에 울릴 수 있는 **소리(스펙)** 수. 겹 수가 아니다.
 *
 * 상한이 필요한 이유는 연타다. 사격은 초당 여러 발이 나가고, 소리 하나가 겹 넷이면
 * 노드가 순식간에 수백 개가 된다. 폰에서 그건 지연이 아니라 **끊김**으로 나타난다.
 *
 * 넘치면 **가장 오래된 소리를 뺏는다**(가장 새 소리를 버리지 않는다). 슈터에서 방금 누른
 * 입력이 조용하면 그건 소리가 아니라 조작이 고장 난 것으로 읽히기 때문이다.
 */
const MAX_CONCURRENT_SOUNDS = 8

/** 뺏을 때의 페이드(초). 즉시 끊으면 파형이 잘려 딸깍 소리가 난다. */
const STEAL_FADE_SECONDS = 0.008

/**
 * 예약 여유(초). `currentTime` 정각에 예약하면 브라우저에 따라 첫 샘플이 잘린다.
 * 5ms는 사람이 못 느끼는 지연이면서 렌더 블록 하나를 넘기기에 충분하다.
 */
const SCHEDULE_LEAD_SECONDS = 0.005

/** `exponentialRampToValueAtTime`은 0으로 갈 수 없다. 사실상 무음인 값. */
const SILENCE = 0.0001

/** 어택 0은 같은 시각에 두 스케줄이 겹쳐 브라우저마다 결과가 갈린다. 최소값을 강제한다. */
const MIN_ATTACK_SECONDS = 0.001

/** 감쇠 꼬리가 완전히 사라진 뒤에 노드를 멈춘다. 정각에 멈추면 딸깍 소리가 난다. */
const RELEASE_PAD_SECONDS = 0.02

/** 볼륨·음소거 전환 페이드(초). 즉시 바꾸면 울리던 소리가 딸깍 하고 끊긴다. */
const GAIN_FADE_SECONDS = 0.02

/*
 * 마스터 소프트 클리퍼.
 *
 * **연타가 클리핑을 만든다.** 소리 하나의 정점은 0.05~0.43이라 여유가 넉넉하지만, 사격을
 * 몰아치면 같은 소리가 여덟 개까지 겹치고(`MAX_CONCURRENT_SOUNDS`) 위상이 맞으면 그대로
 * 여덟 배가 된다. 실측으로 정점 2.4, 1을 넘긴 샘플 235개가 나왔다. 디지털 클리핑은
 * "크다"가 아니라 **"깨졌다"**로 들리고, 하필 가장 신나는 순간에 그 소리가 난다.
 *
 * ## `DynamicsCompressorNode`를 쓰지 않은 이유 (실측)
 *
 * 표준 리미터로 보이는 그 노드를 먼저 붙여 봤는데, 크롬 구현이 **문턱 아래 신호까지 상시
 * 13dB를 깎았다**(입력 0.1 → 출력 0.022, 보고되는 `reduction`은 -0.77dB). 조용한 소리
 * 하나에도 그 대가를 치르는 셈이라, 막으려던 것보다 손해가 컸다. 브라우저마다 다른 값을
 * 보정해 쓰는 것은 그 자체가 버그의 씨앗이라 버렸다.
 *
 * ## 대신 파형을 직접 접는다
 *
 * `WaveShaperNode` 하나에 "무릎까지는 그대로, 그 위는 tanh로 천장에 붙는" 곡선을 굽는다.
 * 브라우저 구현에 기대는 부분이 없고(우리가 만든 배열이다), 지연이 없고, **무릎 아래에서
 * 완전히 투명하다**(실측: 0.2901 → 0.2901). 겹칠 때만 값을 치른다.
 *
 * | 상수 | 올리면 | 내리면 |
 * |---|---|---|
 * | `SOFT_CLIP_KNEE` | 더 넓은 구간이 손대지 않은 소리다(겹침이 거칠어진다) | 일찍 접혀 답답해진다 |
 * | `SOFT_CLIP_HEADROOM` | 더 큰 겹침까지 부드럽게 접는다 | 천장에 평평하게 붙는 지점이 빨라진다 |
 */

/** 이 크기까지는 손대지 않는다. 소리 하나(최대 0.43)와 정상적인 겹침(실측 0.81)이 그 아래다. */
const SOFT_CLIP_KNEE = 0.7

/**
 * 천장.
 *
 * 1.0이 아닌 이유는 **오버샘플링이 넘겨 주기 때문이다.** `oversample: '2x'`가 쓰는
 * 리샘플링 필터는 접힌 파형의 모서리에서 울려서 곡선의 천장보다 15%쯤 위로 튄다
 * (실측: 천장 0.98에서 정점 1.13). 천장을 미리 그만큼 낮춰 두는 것이 이 값의 존재 이유다.
 *
 * 무릎(0.7) 아래는 어차피 손대지 않으므로, 이 값을 낮춰도 **평상시 소리는 하나도 작아지지 않는다.**
 * 값을 치르는 것은 정점이 1을 넘긴 겹침뿐이다.
 */
const SOFT_CLIP_CEILING = 0.85

/**
 * 곡선이 부드럽게 다루는 입력 상한.
 *
 * `WaveShaperNode`는 입력을 -1~1로 자른 뒤 곡선을 찾으므로, 그보다 큰 입력을 다루려면
 * **미리 나눠서 넣고 곡선의 기울기로 되돌려야** 한다. 2면 정점 2.0까지 곡선이 받는다.
 */
const SOFT_CLIP_HEADROOM = 2

/** 곡선 해상도. 4096이면 구간 사이의 계단이 들리지 않는다. */
const SOFT_CLIP_CURVE_POINTS = 4096

/** 노이즈 버퍼 길이(초). 가장 긴 노이즈 겹(워프 스윕 0.35초)보다 넉넉하면 된다. */
const NOISE_SECONDS = 1

/**
 * 노이즈 버퍼의 시드.
 *
 * `Math.random()`을 쓰지 않는다. 판정에 영향은 없지만, 같은 시드에서 **소리까지 같아야**
 * 개발자가 "아까 그 탄피 소리"를 다시 들으며 비교할 수 있다. 재현되지 않는 것은 튜닝할 수 없다.
 */
const NOISE_SEED = 0x9e3779b9

/** 노이즈 재생 시작점을 매번 조금씩 밀어 같은 버스트가 복사된 것처럼 들리지 않게 한다. */
const NOISE_CURSOR_STEP = 0.037

/** 저장 키. `progress.ts`의 `weird-room:progress`와 같은 규약이다. */
export const AUDIO_STORAGE_KEY = 'weird-room:audio'

/**
 * 기본값은 **소리 켬**이다. 근거는 이렇다.
 *
 * 파티게임이 방송·공공장소에서 켜진다는 것은 사실이고, 그래서 음소거 버튼은 HUD에 상설이며
 * 선택은 localStorage에 남는다(스트리머는 **한 번만** 끄면 된다).
 *
 * 그럼에도 기본을 켬으로 두는 이유는 이 게임의 보상 구조다. 5분 동안의 보상이
 * "찾았다" 12번뿐이고, 콤보 피치 상승은 **소리로만 존재하는 장치**다. 기본이 끔이면
 * 대다수가 그것을 한 번도 겪지 못한 채 "밋밋하다"고 판단하고 떠난다. 첫 판이 이 게임의
 * 전부인데 그 첫 판을 무음으로 시작할 이유가 없다.
 *
 * 자동재생 사고도 아니다. AudioContext는 첫 제스처 전에는 만들어지지도 않으므로
 * 페이지를 여는 것만으로는 어떤 소리도 나지 않는다.
 */
const DEFAULT_MUTED = false

/* --- 설정 저장 -------------------------------------------------------------------- */

export type AudioSettings = Readonly<{ muted: boolean; volume: number }>

/**
 * 저장소 배관(접근·파싱 예외를 삼키는 것)은 `storage.ts`가 맡는다. 여기는 **모양 검사와 기본값**만
 * 안다 - 음소거 설정 하나 못 읽었다고 게임이 안 켜지면 안 되므로, 무엇이 와도 기본값으로 접는다.
 */
function clampVolume(value: unknown): number {
  return clampUnit(value, DEFAULT_VOLUME)
}

export function readAudioSettings(): AudioSettings {
  const parsed = readJsonSetting(AUDIO_STORAGE_KEY)
  if (typeof parsed !== 'object' || parsed === null) {
    return { muted: DEFAULT_MUTED, volume: DEFAULT_VOLUME }
  }
  const record = parsed as Readonly<Record<string, unknown>>
  return {
    muted: typeof record['muted'] === 'boolean' ? record['muted'] : DEFAULT_MUTED,
    volume: clampVolume(record['volume']),
  }
}

export function writeAudioSettings(settings: AudioSettings): void {
  writeJsonSetting(AUDIO_STORAGE_KEY, settings)
}

/* --- 엔진 ------------------------------------------------------------------------ */

/** AudioContext 생성기. 테스트가 가짜를 넣는 구멍이며, 이 구멍이 없으면 jsdom에서 테스트가 불가능하다. */
export type AudioContextFactory = () => AudioContext

export type AudioEngineOptions = Readonly<{
  contextFactory?: AudioContextFactory
  /** 초기 설정. 넘기지 않으면 localStorage에서 읽는다(테스트는 저장소를 건드리지 않는다). */
  settings?: AudioSettings
  /** 설정을 저장할지. 테스트는 끈다. */
  persist?: boolean
  /**
   * `dispose()`가 AudioContext까지 닫는가. 기본 `true`.
   * 앱의 싱글턴은 BGM과 컨텍스트를 나눠 쓰므로 `false`다 - 효과음이 닫으면 곡도 같이 죽는다.
   */
  ownsContext?: boolean
}>

/** 재생 중인 소리 하나가 들고 있는 것 전부. 누수는 여기서만 생길 수 있다. */
type ActiveSound = {
  readonly gain: GainNode
  readonly nodes: AudioNode[]
  readonly sources: AudioScheduledSourceNode[]
  /** 아직 끝나지 않은 겹 수. 0이 되면 정리한다. */
  pending: number
  stolen: boolean
}

function defaultContextFactory(): AudioContext {
  const scope = globalThis as {
    AudioContext?: typeof AudioContext
    webkitAudioContext?: typeof AudioContext
  }
  const Ctor = scope.AudioContext ?? scope.webkitAudioContext
  if (Ctor === undefined) throw new Error('AudioContext 없음')
  return new Ctor()
}

/** 무릎까지는 입력 그대로, 그 위는 천장에 점근한다. 무릎에서 값과 기울기가 모두 이어진다. */
function softClip(x: number): number {
  const magnitude = Math.abs(x)
  if (magnitude <= SOFT_CLIP_KNEE) return x

  const range = SOFT_CLIP_CEILING - SOFT_CLIP_KNEE
  return Math.sign(x) * (SOFT_CLIP_KNEE + range * Math.tanh((magnitude - SOFT_CLIP_KNEE) / range))
}

/** `WaveShaperNode`가 먹는 -1~1 격자. 곡선의 원점 기울기가 `SOFT_CLIP_HEADROOM`이다. */
function softClipCurve(): Float32Array<ArrayBuffer> {
  const last = SOFT_CLIP_CURVE_POINTS - 1
  const curve = new Float32Array(SOFT_CLIP_CURVE_POINTS)
  for (let i = 0; i <= last; i += 1) {
    curve[i] = softClip(((i / last) * 2 - 1) * SOFT_CLIP_HEADROOM)
  }
  return curve
}

/**
 * 마스터 게인이 물릴 곳. 소프트 클리퍼를 세워 스피커까지 잇고 그 입구를 돌려준다.
 *
 * 앞의 게인이 헤드룸만큼 **나누고** 곡선의 원점 기울기가 그만큼 **되돌린다.** 둘을 곱하면
 * 1이므로 작은 소리는 이 구간을 지나도 값이 변하지 않는다.
 *
 * 클리퍼를 못 만드는 구현에서는 스피커를 그대로 돌려준다 - 겹칠 때 깨질 수는 있어도
 * **소리가 아예 안 나는 것보다는 낫다.** 이 파일의 모든 실패 경로가 같은 판단이다.
 */
function busTail(ctx: AudioContext): BusTail {
  try {
    const trim = ctx.createGain()
    trim.gain.value = 1 / SOFT_CLIP_HEADROOM

    const shaper = ctx.createWaveShaper()
    shaper.curve = softClipCurve()
    // 비선형이 만든 배음이 나이키스트에 접히는 것을 줄인다. 2배면 충분하고 4배는 폰에서 비싸다.
    shaper.oversample = '2x'

    trim.connect(shaper)
    shaper.connect(ctx.destination)
    return {
      input: trim,
      detach() {
        trim.disconnect()
        shaper.disconnect()
      },
    }
  } catch {
    // 스피커 직결. 뗄 노드가 없다.
    return { input: ctx.destination, detach: () => undefined }
  }
}

/**
 * 버스 꼬리 핸들. `input`에 마스터를 꽂고, `detach()`가 꼬리의 노드를 스피커에서 뗀다.
 * 핸들로 돌려주는 이유: `dispose()`가 마스터만 끊으면 공유 컨텍스트(`ownsContext: false`)의
 * destination에 trim→shaper 쌍이 엔진을 만들 때마다 하나씩 남는다.
 */
type BusTail = Readonly<{ input: AudioNode; detach(): void }>

/**
 * 결정론적 화이트 노이즈. xorshift32 - 짧고, 시드가 같으면 언제나 같은 파형이 나온다.
 */
function fillNoise(data: Float32Array): void {
  let x = NOISE_SEED
  for (let i = 0; i < data.length; i += 1) {
    x ^= x << 13
    x ^= x >>> 17
    x ^= x << 5
    // 32비트 정수를 -1~1로. `>>> 0`로 부호를 없앤 뒤 절반을 빼는 것이 가장 싼 경로다.
    data[i] = ((x >>> 0) / 0xffffffff) * 2 - 1
  }
}

export interface AudioEngine {
  /** 첫 사용자 제스처 안에서 부른다. AudioContext는 **여기서 처음 생긴다.** */
  unlock(): void
  /** 첫 제스처를 자동으로 잡아 `unlock()`한다. 돌려주는 함수를 부르면 구독이 끊긴다. */
  installUnlockGesture(target?: EventTarget): () => void
  /** 소리 하나를 낸다. 실제로 울렸으면 `true`. 음소거·미해제·컨텍스트 없음은 전부 조용한 `false`다. */
  play(spec: SoundSpec): boolean
  isMuted(): boolean
  setMuted(muted: boolean): void
  /** 바뀐 뒤의 음소거 상태를 돌려준다. HUD 버튼이 이것만 부르면 된다. */
  toggleMute(): boolean
  getVolume(): number
  setVolume(volume: number): void
  /** 지금 울리고 있는 소리 수. 동시 발음 상한 검증용. */
  activeSoundCount(): number
  /** 소리를 낼 수 있는 상태인가(컨텍스트가 살아 있고 해제됐는가). HUD가 버튼을 숨길 때 쓴다. */
  isAvailable(): boolean
  dispose(): void
}

export function createAudioEngine(options: AudioEngineOptions = {}): AudioEngine {
  const factory = options.contextFactory ?? defaultContextFactory
  const persist = options.persist ?? true
  const ownsContext = options.ownsContext ?? true

  const initial = options.settings ?? readAudioSettings()
  let muted = initial.muted
  let volume = clampVolume(initial.volume)

  let context: AudioContext | null = null
  let master: GainNode | null = null
  let tail: BusTail | null = null
  let noise: AudioBuffer | null = null
  let noiseCursor = 0
  let unlocked = false
  /** 한 번 실패한 생성을 매 프레임 다시 시도하면 그 자체가 렉이다. */
  let broken = false

  const active: ActiveSound[] = []

  function targetGain(): number {
    return muted ? 0 : volume
  }

  function ensureContext(): AudioContext | null {
    if (context !== null) return context
    if (broken) return null

    try {
      const ctx = factory()
      const gain = ctx.createGain()
      gain.gain.value = targetGain()
      const bus = busTail(ctx)
      gain.connect(bus.input)
      context = ctx
      master = gain
      tail = bus
      return ctx
    } catch {
      // 구형 브라우저·정책 차단. 소리는 없지만 게임은 계속된다.
      broken = true
      context = null
      master = null
      tail = null
      return null
    }
  }

  function resumeQuietly(ctx: AudioContext): void {
    try {
      void ctx.resume().catch(() => undefined)
    } catch {
      // resume 자체가 동기로 던지는 구형 구현이 있다. 조용히 넘긴다.
    }
  }

  function unlock(): void {
    const ctx = ensureContext()
    if (ctx === null) return
    unlocked = true
    resumeQuietly(ctx)
  }

  /**
   * 첫 제스처 뒤에 구독이 남는 경우는 없다 - `unlock()`은 컨텍스트를 세웠든(`unlocked`) 못 세웠든
   * (`broken`) 둘 중 하나로 끝나고, 어느 쪽이든 다시 시도할 이유가 없다. 그래서 조건 없이 걷는다.
   */
  function installUnlockGesture(target?: EventTarget): () => void {
    return installGestureOnce(target, unlock)
  }

  function ensureNoise(ctx: AudioContext): AudioBuffer | null {
    if (noise !== null) return noise
    try {
      const buffer = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * NOISE_SECONDS), ctx.sampleRate)
      fillNoise(buffer.getChannelData(0))
      noise = buffer
      return buffer
    } catch {
      return null
    }
  }

  /** 재생이 끝났거나 뺏긴 소리의 노드를 전부 끊는다. **누수는 이 함수가 안 불리면 생긴다.** */
  function release(sound: ActiveSound): void {
    for (const node of sound.nodes) {
      try {
        node.disconnect()
      } catch {
        // 이미 끊긴 노드. 무시한다.
      }
    }
    sound.nodes.length = 0
    sound.sources.length = 0
    const at = active.indexOf(sound)
    if (at >= 0) active.splice(at, 1)
  }

  /** 상한을 넘겼을 때 가장 오래된 소리를 짧게 페이드아웃하고 물러나게 한다. */
  function steal(ctx: AudioContext): void {
    const oldest = active.shift()
    if (oldest === undefined || oldest.stolen) return
    oldest.stolen = true

    const now = ctx.currentTime
    try {
      oldest.gain.gain.setValueAtTime(Math.max(oldest.gain.gain.value, SILENCE), now)
      oldest.gain.gain.exponentialRampToValueAtTime(SILENCE, now + STEAL_FADE_SECONDS)
      for (const source of oldest.sources) source.stop(now + STEAL_FADE_SECONDS)
    } catch {
      // 예약에 실패하면 그냥 지금 정리한다. 소리 하나가 남는 것보다 노드가 남는 게 나쁘다.
      release(oldest)
    }
  }

  function scheduleEnvelope(param: AudioParam, t0: number, attack: number, decay: number, peak: number): void {
    param.setValueAtTime(0, t0)
    param.linearRampToValueAtTime(peak, t0 + attack)
    param.exponentialRampToValueAtTime(SILENCE, t0 + attack + decay)
  }

  function scheduleGlide(param: AudioParam, from: number, to: number | undefined, t0: number, t1: number): void {
    param.setValueAtTime(from, t0)
    if (to === undefined || to === from) return
    // 지수 램프인 이유: 사람 귀는 주파수를 로그로 듣는다. 선형 스윕은 위쪽이 뭉개진다.
    param.exponentialRampToValueAtTime(Math.max(to, SILENCE), t1)
  }

  function buildVoice(ctx: AudioContext, sound: ActiveSound, voice: Voice, base: number): boolean {
    const t0 = base + (voice.delay ?? 0)
    const attack = Math.max(voice.attack ?? 0, MIN_ATTACK_SECONDS)
    const end = t0 + attack + voice.decay

    let source: AudioScheduledSourceNode
    if (voice.source.kind === 'tone') {
      const osc = ctx.createOscillator()
      osc.type = voice.source.wave
      scheduleGlide(osc.frequency, voice.source.hz, voice.source.hzTo, t0, end)
      source = osc
    } else {
      const buffer = ensureNoise(ctx)
      if (buffer === null) return false
      const player = ctx.createBufferSource()
      player.buffer = buffer
      // 같은 지점에서 늘 잘라 쓰면 버스트가 복사된 것처럼 들린다. 결정론을 지키며 커서만 민다.
      noiseCursor = (noiseCursor + NOISE_CURSOR_STEP) % (NOISE_SECONDS / 2)
      source = player
    }

    const envelope = ctx.createGain()
    scheduleEnvelope(envelope.gain, t0, attack, voice.decay, voice.gain)

    let tail: AudioNode = envelope
    source.connect(envelope)

    if (voice.filter !== undefined) {
      const filter = ctx.createBiquadFilter()
      filter.type = voice.filter.kind
      scheduleGlide(filter.frequency, voice.filter.hz, voice.filter.hzTo, t0, end)
      if (voice.filter.q !== undefined) filter.Q.value = voice.filter.q
      envelope.connect(filter)
      tail = filter
      sound.nodes.push(filter)
    }

    tail.connect(sound.gain)
    sound.nodes.push(source, envelope)
    sound.sources.push(source)
    sound.pending += 1

    source.onended = (): void => {
      sound.pending -= 1
      if (sound.pending <= 0) release(sound)
    }

    if (voice.source.kind === 'noise') {
      // 버퍼 소스는 길이를 인자로 받는다. 그래야 1초짜리 버퍼를 30ms만 쓸 수 있다.
      ;(source as AudioBufferSourceNode).start(t0, noiseCursor, attack + voice.decay + RELEASE_PAD_SECONDS)
    } else {
      source.start(t0)
    }
    source.stop(end + RELEASE_PAD_SECONDS)
    return true
  }

  function play(spec: SoundSpec): boolean {
    // 음소거면 **노드를 하나도 만들지 않는다.** 마스터 게인을 0으로 두고 그냥 트는 구현은
    // 조용하기만 할 뿐 폰의 오디오 스레드를 그대로 먹는다.
    if (muted) return false
    if (!unlocked || broken) return false

    const ctx = context
    if (ctx === null || ctx.state === 'closed') return false
    // 탭 복귀·정책 재정지로 다시 suspended가 되는 일이 있다. 재개를 걸고 예약은 그대로 한다
    // (currentTime이 멈춰 있으므로 재개되는 순간 제 위치에서 울린다).
    if (ctx.state === 'suspended') resumeQuietly(ctx)

    while (active.length >= MAX_CONCURRENT_SOUNDS) steal(ctx)

    let sound: ActiveSound
    try {
      const gain = ctx.createGain()
      gain.gain.value = spec.gain ?? 1
      gain.connect(master ?? ctx.destination)
      sound = { gain, nodes: [gain], sources: [], pending: 0, stolen: false }
    } catch {
      return false
    }

    active.push(sound)

    const base = ctx.currentTime + SCHEDULE_LEAD_SECONDS
    let built = 0
    for (const voice of spec.voices) {
      try {
        if (buildVoice(ctx, sound, voice, base)) built += 1
      } catch {
        // 겹 하나가 실패해도 나머지는 울린다. 소리가 얇아질 뿐 게임은 계속된다.
      }
    }

    if (built === 0) {
      release(sound)
      return false
    }
    return true
  }

  function applyGain(): void {
    const ctx = context
    const gain = master
    if (ctx === null || gain === null) return
    try {
      // 지금 값에 말뚝을 박고 램프를 건다. 말뚝이 없으면 **직전 램프의 목표에서** 출발해
      // 음소거를 연타할 때 게인이 튄다(HUD 버튼은 연타되는 물건이다).
      const now = ctx.currentTime
      gain.gain.setValueAtTime(gain.gain.value, now)
      gain.gain.linearRampToValueAtTime(targetGain(), now + GAIN_FADE_SECONDS)
    } catch {
      gain.gain.value = targetGain()
    }
  }

  function save(): void {
    if (persist) writeAudioSettings({ muted, volume })
  }

  return {
    unlock,
    installUnlockGesture,
    play,
    isMuted: () => muted,
    setMuted(next: boolean): void {
      if (next === muted) return
      muted = next
      applyGain()
      save()
    },
    toggleMute(): boolean {
      muted = !muted
      applyGain()
      save()
      return muted
    },
    getVolume: () => volume,
    setVolume(next: number): void {
      const clamped = clampVolume(next)
      if (clamped === volume) return
      volume = clamped
      applyGain()
      save()
    },
    activeSoundCount: () => active.length,
    isAvailable: () => unlocked && !broken && context !== null && context.state !== 'closed',
    dispose(): void {
      for (const sound of [...active]) release(sound)
      const ctx = context
      const gain = master
      const bus = tail
      context = null
      master = null
      tail = null
      noise = null
      unlocked = false
      // 마스터와 버스 꼬리를 함께 끊어야 공유 컨텍스트에 이 엔진의 노드가 남지 않는다.
      // 마스터만 끊으면 trim→shaper→destination 체인이 그대로 남는다. 닫는 경우에도 해롭지 않다.
      try {
        gain?.disconnect()
      } catch {
        // 이미 끊긴 노드.
      }
      try {
        bus?.detach()
      } catch {
        // 이미 끊긴 노드.
      }
      // 공유 컨텍스트는 **닫지 않는다**(`bgm.ts`의 dispose와 같은 규약). 참조를 놓은 것으로 끝이다.
      if (ctx === null || !ownsContext) return
      try {
        void ctx.close().catch(() => undefined)
      } catch {
        // 이미 닫혔거나 close를 모르는 구현. 참조를 놓은 것으로 충분하다.
      }
    },
  }
}

/**
 * 앱이 쓰는 하나뿐인 엔진.
 *
 * 모듈 로드 시점에 만들어도 안전하다 - 생성자는 localStorage만 읽고 **AudioContext는 만들지 않는다.**
 * UI가 인스턴스를 들고 다니지 않아도 되게 하려고 둔다. 테스트는 `createAudioEngine`을 직접 부른다.
 *
 * **컨텍스트는 BGM과 나눠 쓴다.** 둘이 각자 `AudioContext`를 만들면 모바일에서 자동재생 해제가
 * 한쪽에만 걸려 소리 하나가 통째로 안 나고, 컨텍스트마다 오디오 스레드가 붙어 지연도 늘어난다.
 * `bgm.ts`는 이쪽에서 타입만 가져가므로(`import type`) 런타임 순환이 아니다.
 * 공유 컨텍스트라 `dispose()`가 닫지 않는다(`ownsContext: false`) - 효과음이 닫으면 곡도 같이 죽는다.
 */
export const audio: AudioEngine = createAudioEngine({ contextFactory: sharedAudioContext, ownsContext: false })
